/**
 * Interaction utility functions for floor plan user interactions
 */

import { isSink } from './sinkHelpers';
import {
  isPointNearWall,
  isPointNearWindow,
  isPointNearOpening,
  isPointInObjectBounds,
  isPointInSinkBounds,
  screenToWorldCoordinates,
} from './geometryUtils';
import { isPointNearDoor, isPointNearDoorV2 } from './doorUtils';
import { buildOpeningLookup, resolveRoomOpening } from './openingSyncUtils';
import { chooseRoomLabelAnchors } from '@shared-lib/geometry/roomLabelAnchor';

/**
 * Detects which element was clicked on the floor plan
 * @param {number} worldX - World X coordinate
 * @param {number} worldY - World Y coordinate
 * @param {Object} data - Structure data containing all elements
 * @param {boolean} showRoomLabels - Whether room labels are visible
 * @returns {Object|null} Clicked element with type tag or null
 */
export const detectClickedElement = (
  worldX,
  worldY,
  data,
  showRoomLabels = false,
  structureWalls = []
) => {
  const allClickableItems = [];

  // Check if we have room-based data structure
  const hasRoomStructure =
    data.rooms &&
    data.rooms.length > 0 &&
    data.rooms.some(
      (room) => room.walls || room.objects || room.doors || room.windows
    );

  // Post-slim-refactor: room-side opening copies hold only
  // {id, structureOpeningId, parentWallIds, category} — geometry lives on
  // the structure-side copy. Hit-testing needs center/dimensions/rotation,
  // so build a lookup of canonical structure openings once and resolve
  // each iterated room copy before passing to the point-test helpers.
  // Structure-level entries (data.doors / windows / openings) are reached
  // via their corresponding room copies; any structure entry without a
  // room copy (exterior, orphan) is also appended at the end so it remains
  // clickable.
  const structureOpeningLookup = buildOpeningLookup({
    doors: data.doors,
    windows: data.windows,
    openings: data.openings,
  });

  const collectRoomOpenings = (kind) => {
    const seenStructureIds = new Set();
    const collected = [];
    if (hasRoomStructure && data.rooms) {
      data.rooms.forEach((room) => {
        const arr = room[kind];
        if (!arr || arr.length === 0) return;
        arr.forEach((item) => {
          if (item.structureOpeningId) {
            const structureItem = structureOpeningLookup.get(
              item.structureOpeningId
            );
            if (!structureItem) return; // orphan room copy; skip
            collected.push({
              ...resolveRoomOpening(item, structureItem),
              _roomId: room.id,
            });
            seenStructureIds.add(item.structureOpeningId);
          } else {
            // Legacy non-slim room copy (pre-refactor data) — keep as-is.
            collected.push({ ...item, _roomId: room.id });
          }
        });
      });
    }
    const structureArr = data[kind];
    if (structureArr && structureArr.length > 0) {
      for (const item of structureArr) {
        if (!seenStructureIds.has(item.id)) {
          collected.push(item);
        }
      }
    }
    return collected;
  };

  const allDoors = collectRoomOpenings('doors');

  // Check doors first (highest priority)
  for (const door of allDoors) {
    // Use V2 function for doors with center/rotation format, otherwise use old function
    const hasNewFormat = door.center && door.rotation !== undefined;
    const checkFunction = hasNewFormat ? isPointNearDoorV2 : isPointNearDoor;

    if (checkFunction(worldX, worldY, door)) {
      allClickableItems.push({ ...door, itemType: 'door' });
    }
  }

  const allWindows = collectRoomOpenings('windows');

  // Check windows with tighter threshold
  for (const window of allWindows) {
    const isNear = isPointNearWindow(worldX, worldY, window);
    if (isNear) {
      // Ensure window has proper type indicators for selection
      const windowWithType = {
        ...window,
        itemType: 'window',
        isWindow: true, // Add explicit isWindow flag
        category: window.category || { window: {} }, // Ensure category exists
      };
      allClickableItems.push(windowWithType);
    }
  }

  const allOpenings = collectRoomOpenings('openings');

  // Check openings with tighter threshold
  for (const opening of allOpenings) {
    if (isPointNearOpening(worldX, worldY, opening, 0.15)) {
      // 15cm threshold
      const distance = opening.center
        ? Math.sqrt(
            Math.pow(worldX - opening.center.x, 2) +
              Math.pow(worldY - opening.center.z, 2)
          )
        : Infinity;
      // Tag as opening for identification
      allClickableItems.push({
        ...opening,
        itemType: 'opening',
        isOpening: true,
      });
    }
  }

  // Continue checking other elements - don't return early

  // Collect structure walls only for click detection
  // Room walls are surface veneers and should not be directly selectable
  let allWalls = [];
  if (structureWalls && structureWalls.length > 0) {
    structureWalls.forEach((sw) => {
      // Mark as structure wall for identification
      allWalls.push({ ...sw, _isStructureWall: true });
    });
  }

  // Check walls
  for (const wall of allWalls) {
    if (isPointNearWall(worldX, worldY, wall)) {
      // Tag as wall for identification
      allClickableItems.push({ ...wall, itemType: 'wall', isWall: true });
    }
  }

  // Check room labels if they are visible. Hit-test against every
  // anchor the renderer would draw a label at — `chooseRoomLabelAnchors`
  // is the shared policy (`shared-lib/geometry/roomLabelAnchor.js`) used
  // by both `drawRoomLabels` and the pipeline debug pages, so clicks land
  // wherever the user sees the label.
  if (showRoomLabels && data.rooms && data.rooms.length > 0) {
    const clickRadius = 0.5;
    for (const room of data.rooms) {
      for (const anchor of chooseRoomLabelAnchors(room)) {
        const distance = Math.hypot(
          worldX - anchor.point.x,
          worldY - anchor.point.z
        );
        if (distance <= clickRadius) {
          allClickableItems.push({
            ...room,
            itemType: 'roomLabel',
            isRoomLabel: true,
          });
          break;
        }
      }
    }
  }

  // Collect all objects
  let allObjects = [];
  if (hasRoomStructure && data.rooms) {
    // Get objects from rooms with room context
    data.rooms.forEach((room) => {
      if (room.objects && room.objects.length > 0) {
        room.objects.forEach((obj) => {
          allObjects.push({ ...obj, _roomId: room.id });
        });
      }
    });
  }
  // Also include flattened objects if available
  if (data.objects && data.objects.length > 0) {
    allObjects = allObjects.concat(data.objects);
  }

  // Check sinks in new location (room.objects[] with parents array)
  if (allObjects.length > 0) {
    const clickedSinks = [];
    allObjects.forEach((obj) => {
      if (isSink(obj) && obj.parents) {
        // This is a sink in room.objects[] with parents array
        const position = obj.center || obj.position;
        if (
          position &&
          isPointInSinkBounds(worldX, worldY, { ...obj, position })
        ) {
          // Find parent countertop for metadata
          const countertopParent = obj.parents?.find(
            (p) => p.type === 'countertop'
          );

          // Add to clickable items with proper metadata
          clickedSinks.push({
            ...obj,
            itemType: 'sink',
            countertopId: countertopParent?.id,
          });
        }
      }
    });

    // Add all clicked sinks to allObjects
    allObjects.push(...clickedSinks);
  }

  // Track IDs of sinks already detected to avoid duplicates
  const clickedSinkIds = new Set(
    allObjects.filter((o) => o.itemType === 'sink').map((s) => s.id)
  );

  // Check objects (lowest priority, but consider z-order for overlapping objects)
  if (allObjects.length > 0) {
    // First, collect all objects that the point is within
    const clickedObjects = allObjects.filter((obj) => {
      // Sinks were already checked and added if clicked, so include them
      if (obj.itemType === 'sink') {
        return true;
      }
      // Skip original sink objects that were already detected above
      // to prevent duplicates in the overlapping items list
      if (isSink(obj) && clickedSinkIds.has(obj.id)) {
        return false;
      }
      return isPointInObjectBounds(worldX, worldY, obj);
    });

    if (clickedObjects.length > 0) {
      // Sort by priority: bathroom fixtures first
      const prioritizedObjects = clickedObjects.sort((a, b) => {
        const getObjectType = (obj) => {
          // Check attributes first for most accurate type
          if (obj.attributes?.ChairType) return 'chair';
          if (obj.attributes?.TableType) return 'table';
          if (obj.attributes?.SofaType) return 'sofa';

          if (obj.category && typeof obj.category === 'object') {
            // Get the first key from the category object
            const keys = Object.keys(obj.category);
            if (keys.length > 0) {
              return keys[0].toLowerCase();
            }
          }
          // Fallback checks
          if (obj.type) return obj.type.toLowerCase();
          if (obj.objectType) return obj.objectType.toLowerCase();
          return 'unknown';
        };

        const typeA = getObjectType(a);
        const typeB = getObjectType(b);

        // For overlapping furniture, use more sophisticated logic
        if (
          (typeA === 'chair' || typeA === 'table') &&
          (typeB === 'chair' || typeB === 'table')
        ) {
          // Always prefer chairs over tables when they overlap (chairs are tucked under tables)
          if (typeA === 'chair' && typeB === 'table') return -1;
          if (typeA === 'table' && typeB === 'chair') return 1;

          // If same type, prefer smaller objects
          const volumeA = (a.dimensions?.x || 1) * (a.dimensions?.z || 1);
          const volumeB = (b.dimensions?.x || 1) * (b.dimensions?.z || 1);

          if (volumeA < volumeB) return -1;
          if (volumeA > volumeB) return 1;
        }

        // Check if bathroom fixtures - note 'sink' should be one of the category keys
        const isBathroomA =
          typeA.includes('sink') ||
          typeA === 'toilet' ||
          typeA === 'bathtub' ||
          typeA === 'bath';
        const isBathroomB =
          typeB.includes('sink') ||
          typeB === 'toilet' ||
          typeB === 'bathtub' ||
          typeB === 'bath';
        const isStorageA =
          typeA === 'storage' ||
          typeA === 'cabinet' ||
          typeA === 'shelf' ||
          typeA === 'bookshelf';
        const isStorageB =
          typeB === 'storage' ||
          typeB === 'cabinet' ||
          typeB === 'shelf' ||
          typeB === 'bookshelf';

        // Bathroom fixtures have highest priority
        if (isBathroomA && !isBathroomB) return -1;
        if (!isBathroomA && isBathroomB) return 1;

        // Storage has lowest priority
        if (isStorageA && !isStorageB) return 1;
        if (!isStorageA && isStorageB) return -1;

        // Manual cabinet runs: a click should land on the cabinet UNIT first,
        // not the countertop drawn on top of it (a second click cycles to the
        // countertop). Scope to run objects (runId) so generator-built kitchen
        // selection is unchanged.
        if (a.runId && b.runId) {
          const aIsCab =
            typeA === 'lower-cabinet' || typeA === 'corner-cabinet';
          const bIsCab =
            typeB === 'lower-cabinet' || typeB === 'corner-cabinet';
          if (aIsCab && typeB === 'countertop') return -1;
          if (typeA === 'countertop' && bIsCab) return 1;
        }

        // A stacked upper (`category.stack`) has the footprint of the upper
        // it stands on: a click lands on the UPPER, whose panel has the bay
        // controls; a second click cycles to the box over it.
        if (!a.category?.stack !== !b.category?.stack)
          return a.category?.stack ? 1 : -1;

        // If same type, prefer objects that appear later in the array (drawn on top)
        return allObjects.indexOf(b) - allObjects.indexOf(a);
      });

      // Add all objects to the clickable items
      clickedObjects.forEach((obj) => {
        allClickableItems.push({ ...obj, itemType: 'object' });
      });
    }
  }

  // If we found any items, determine the primary one based on priority
  if (allClickableItems.length > 0) {
    // Priority order: doors > windows > openings > walls > room labels > objects
    // Within objects, bathroom fixtures > other objects > storage

    let primaryItem = allClickableItems[0];

    // Find highest priority item
    for (const item of allClickableItems) {
      if (item.itemType === 'door') {
        primaryItem = item;
        break;
      } else if (
        item.itemType === 'window' &&
        primaryItem.itemType !== 'door'
      ) {
        primaryItem = item;
      } else if (
        item.itemType === 'opening' &&
        primaryItem.itemType !== 'door' &&
        primaryItem.itemType !== 'window'
      ) {
        primaryItem = item;
      } else if (
        item.itemType === 'wall' &&
        primaryItem.itemType !== 'door' &&
        primaryItem.itemType !== 'window' &&
        primaryItem.itemType !== 'opening' &&
        primaryItem.itemType !== 'wall'
      ) {
        // Only structure walls are in click detection (room walls are surface veneers)
        primaryItem = item;
      } else if (
        item.itemType === 'roomLabel' &&
        primaryItem.itemType === 'object'
      ) {
        primaryItem = item;
      } else if (
        item.itemType === 'object' &&
        primaryItem.itemType === 'object'
      ) {
        // Handle object priority (bathroom fixtures first)
        const getObjectType = (obj) => {
          if (obj.category && typeof obj.category === 'object') {
            const keys = Object.keys(obj.category);
            return keys.length > 0 ? keys[0].toLowerCase() : 'unknown';
          }
          return (
            obj.type?.toLowerCase() ||
            obj.objectType?.toLowerCase() ||
            'unknown'
          );
        };

        const primaryType = getObjectType(primaryItem);
        const itemType = getObjectType(item);

        const isBathroomPrimary =
          primaryType.includes('sink') ||
          ['toilet', 'bathtub', 'bath'].includes(primaryType);
        const isBathroomItem =
          itemType.includes('sink') ||
          ['toilet', 'bathtub', 'bath'].includes(itemType);

        if (!isBathroomPrimary && isBathroomItem) {
          primaryItem = item;
        }
      }
    }

    return {
      primary: primaryItem,
      all: allClickableItems,
    };
  }

  return { primary: null, all: [] };
};

/**
 * Handles canvas click events and element selection
 * @param {MouseEvent} e - Mouse event
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {Object} data - Structure data
 * @param {Object} pan - Current pan offset
 * @param {number} scale - Current zoom scale
 * @param {number} rotation - Current rotation in degrees
 * @param {Function} onItemSelect - Callback for item selection
 * @param {boolean} showRoomLabels - Whether room labels are visible
 * @returns {Object} Selection result with element and world coordinates
 */
export const handleCanvasClick = (
  e,
  canvas,
  data,
  pan,
  scale,
  rotation,
  onItemSelect,
  showRoomLabels = false,
  structureWalls = []
) => {
  if (!data || !canvas) return { element: null, worldX: null, worldY: null };

  const rect = canvas.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  // Convert screen coordinates to world coordinates
  const worldCoords = screenToWorldCoordinates(
    mouseX,
    mouseY,
    canvas,
    pan,
    scale,
    rotation
  );

  // Detect clicked element
  const result = detectClickedElement(
    worldCoords.x,
    worldCoords.y,
    data,
    showRoomLabels,
    structureWalls
  );

  // Call selection callback if provided
  if (onItemSelect) {
    onItemSelect(result.primary, result.all);
  }

  return {
    element: result.primary,
    allElements: result.all,
    worldX: worldCoords.x,
    worldY: worldCoords.y,
  };
};

/**
 * Creates pan handling functions for canvas dragging
 * @param {Function} setPan - Pan state setter
 * @param {Function} setIsDragging - Dragging state setter
 * @param {Function} setDragStart - Drag start position setter
 * @param {Function} setScale - Scale state setter (optional, for pinch zoom)
 * @returns {Object} Mouse and touch event handlers
 */
export const createPanHandlers = (
  setPan,
  setIsDragging,
  setDragStart,
  setScale = null
) => {
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e, isDragging, dragStart) => {
    if (!isDragging) return;

    // Get the movement in screen coordinates
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    setPan((prevPan) => ({
      x: prevPan.x + dx,
      y: prevPan.y + dy,
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Touch event handlers
  let touchDragStart = null;
  let lastPinchDistance = null;

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch - start panning
      const touch = e.touches[0];
      setIsDragging(true);
      touchDragStart = { x: touch.clientX, y: touch.clientY };
      setDragStart(touchDragStart);
      lastPinchDistance = null;
    } else if (e.touches.length === 2 && setScale) {
      // Two touches - start pinch zoom
      setIsDragging(false);
      touchDragStart = null;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDistance = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handleTouchMove = (e) => {
    // Only prevent default if we're actively dragging or pinching
    if (
      (e.touches.length === 1 && touchDragStart) ||
      (e.touches.length === 2 && lastPinchDistance !== null)
    ) {
      e.preventDefault(); // Prevent scrolling
    }

    if (e.touches.length === 1 && touchDragStart) {
      // Single touch - pan
      const touch = e.touches[0];

      // Get the movement in screen coordinates
      const dx = touch.clientX - touchDragStart.x;
      const dy = touch.clientY - touchDragStart.y;

      setPan((prevPan) => ({
        x: prevPan.x + dx,
        y: prevPan.y + dy,
      }));

      touchDragStart = { x: touch.clientX, y: touch.clientY };
      setDragStart(touchDragStart);
    } else if (
      e.touches.length === 2 &&
      setScale &&
      lastPinchDistance !== null
    ) {
      // Two touches - pinch zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);

      // Calculate zoom factor
      const scaleFactor = currentDistance / lastPinchDistance;

      // Apply zoom with limits (similar to zoom controls)
      setScale((prevScale) => {
        const zoomSpeed = 0.5; // Adjust this to control zoom sensitivity
        const deltaScale = (scaleFactor - 1) * prevScale * zoomSpeed;
        const newScale = prevScale + deltaScale;

        // Clamp between min and max (same limits as zoom controls)
        return Math.min(1000, Math.max(10, newScale));
      });

      lastPinchDistance = currentDistance;
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      // All touches ended
      setIsDragging(false);
      touchDragStart = null;
      lastPinchDistance = null;
    } else if (e.touches.length === 1) {
      // Still one touch left - switch back to panning
      const touch = e.touches[0];
      setIsDragging(true);
      touchDragStart = { x: touch.clientX, y: touch.clientY };
      setDragStart(touchDragStart);
      lastPinchDistance = null;
    }
  };

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};

/**
 * Creates zoom handling functions
 * @param {Function} setScale - Scale state setter
 * @param {number} minScale - Minimum zoom scale
 * @param {number} maxScale - Maximum zoom scale
 * @param {number} zoomStep - Zoom step size
 * @returns {Object} Zoom control functions
 */
export const createZoomHandlers = (
  setScale,
  minScale = 10,
  maxScale = 1000,
  zoomStep = 20
) => {
  const zoomIn = () => {
    setScale((prevScale) => Math.min(maxScale, prevScale + zoomStep));
  };

  const zoomOut = () => {
    setScale((prevScale) => Math.max(minScale, prevScale - zoomStep));
  };

  const zoomReset = (setPan, defaultScale = 70) => {
    setPan({ x: 0, y: 0 });
    setScale(defaultScale);
  };

  const handleWheel = (e) => {
    e.preventDefault();

    // Determine zoom direction
    const delta = e.deltaY > 0 ? -1 : 1;

    // Apply zoom
    setScale((prevScale) => {
      const newScale = prevScale + delta * zoomStep;
      return Math.min(maxScale, Math.max(minScale, newScale));
    });
  };

  return {
    zoomIn,
    zoomOut,
    zoomReset,
    handleWheel,
  };
};

/**
 * Creates keyboard navigation handlers
 * @param {Object} handlers - Object containing pan and zoom handlers
 * @returns {Function} Keyboard event handler
 */
export const createKeyboardHandler = (handlers) => {
  return (e) => {
    switch (e.key) {
      case '+':
      case '=':
        handlers.zoomIn();
        break;
      case '-':
      case '_':
        handlers.zoomOut();
        break;
      case '0':
        handlers.zoomReset();
        break;
      case 'ArrowUp':
        handlers.panUp();
        break;
      case 'ArrowDown':
        handlers.panDown();
        break;
      case 'ArrowLeft':
        handlers.panLeft();
        break;
      case 'ArrowRight':
        handlers.panRight();
        break;
      default:
        break;
    }
  };
};
